import { useMutation } from "@apollo/client";
import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Banner,
  Button,
  Divider,
  Page,
  Text,
  useFlashMessages,
} from "@towersystems/roam-common-native";
import {
  InvoiceArrival,
  InvoiceArrivalItem,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { useFormContext } from "react-hook-form";
import { Colors, View } from "react-native-ui-lib";
import { EditInvoiceLineItemRootScreenParamList } from "../../../../types";
import ProductModel from "../../../../utilities/data/model/Product";
import { useTranslation } from "../../../../utilities/i18n";
import { ProductSummaryCard } from "../../../products/components";
import { InvoiceArrivalItemForm } from "../../components/InvoiceArrivalItemForm";
import { InvoiceArrivalItemFormValues } from "../../components/InvoiceArrivalItemForm/types";
import { MUTATION_UPDATE_INVOICE_ARRIVAL_LINE_ITEMS } from "../../utils";
import {
  useActiveInvoiceArrivalItemProvider,
  useInvoiceArrivalContextProvider,
} from "../../utils/active-invoice-arrival";
import { invoiceLineItemDataToGraphqlInput } from "../../utils/utils";

export type EditInvoiceLineItemScreenProps = StackScreenProps<
  EditInvoiceLineItemRootScreenParamList,
  "EditInvoiceLineItemScreen"
>;

const EditInvoiceLineItem = ({
  lineItem,
  navigation,
}: EditInvoiceLineItemScreenProps & {
  lineItem: InvoiceArrivalItem;
}) => {
  const { t } = useTranslation("invoice_arrival");

  const database = useDatabase();

  const [existingProduct, setExistingProduct] = React.useState<
    ProductModel | undefined
  >(undefined);

  const {
    handleSubmit,
    formState,
    setValue,
    watch,
    clearErrors,
    setError,
    trigger,
  } = useFormContext<InvoiceArrivalItemFormValues>();

  const { isDirty, isLoading, isSubmitting, isValid, errors } = formState;

  const { invoiceArrival, setInvoiceArrival } =
    useInvoiceArrivalContextProvider();

  const [m] = useMutation<{ updateInvoiceArrivalLineItems: InvoiceArrival }>(
    MUTATION_UPDATE_INVOICE_ARRIVAL_LINE_ITEMS
  );

  const watchedProductId = watch("productId");
  const watchedStockNumber = watch("barcode");

  React.useEffect(() => {
    clearErrors("root");

    if (watchedProductId) {
      setExistingProduct(undefined);
      return;
    }

    database
      .get<ProductModel>("products")
      .query(Q.where("stock_number", Q.eq(watchedStockNumber)))
      .then((products) => {
        if (products.length) {
          setExistingProduct(products[0]);
          setError("root.barcode", {
            type: "manual",
            message: t("error.barcode_exists"),
          });
        } else {
          setExistingProduct(undefined);
        }
      });
  }, [watchedStockNumber, watchedProductId, setError]);

  const flashMessagesContext = useFlashMessages();

  const handleOnRemoveLineItem = React.useCallback(async () => {
    try {
      const result = await m({
        variables: {
          id: invoiceArrival.id,
          itemsToRemove: [lineItem.id],
        },
      });

      if (result.data?.updateInvoiceArrivalLineItems) {
        setInvoiceArrival(result.data?.updateInvoiceArrivalLineItems);
      }

      navigation.goBack();
    } catch (err: any) {
      flashMessagesContext.showMessage({
        message: err.message ?? t("common:error"),
        severity: "error",
      });
      setError("root.serverError", {
        type: "400",
        message: err.message ?? t("common:error"),
      });
    }
  }, [invoiceArrival.id, lineItem.id]);

  React.useEffect(() => {
    if (errors.root?.serverError) {
      trigger();
    }
  }, [errors.root?.serverError]);

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => {
        return (
          <Button
            label={t("button.remove_line_item")}
            isDestructive
            disabled={false}
            onPress={handleOnRemoveLineItem}
            size="compact"
          ></Button>
        );
      },
    });
  }, []);

  const handleOnSubmit = React.useCallback(
    async (input: InvoiceArrivalItemFormValues) => {
      try {
        const result = await m({
          variables: {
            id: invoiceArrival.id,
            itemsToUpdate: [
              {
                ...invoiceLineItemDataToGraphqlInput(input),
              },
            ],
          },
        });
        if (result.data?.updateInvoiceArrivalLineItems) {
          setInvoiceArrival(result.data?.updateInvoiceArrivalLineItems);
          navigation.goBack();
        } else {
          throw new Error(t("common:error"));
        }
      } catch (err: any) {
        flashMessagesContext.showMessage({
          message: err.message ?? t("common:error"),
          severity: "error",
        });
        setError("root.serverError", {
          type: "400",
          message: t("common:error"),
        });
      }
    },
    [invoiceArrival.id]
  );

  const matchingProductFoundMarkup = existingProduct ? (
    <Banner
      title={t("banner.matching_product.title")}
      status="warning"
      onPress={async () => {
        setValue("productId", existingProduct.id, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setExistingProduct(undefined);
      }}
    >
      <Text>
        {t("banner.matching_product.description", {
          stock_number: watchedStockNumber,
        })}
      </Text>
    </Banner>
  ) : undefined;

  const nonMatchingMarkup = (
    <Banner status="warning" title="Non-Matched Product">
      <Text>{t("banner.non_matching_product.description")}</Text>
    </Banner>
  );

  const bannerMarkup =
    matchingProductFoundMarkup ??
    (!watchedProductId ? nonMatchingMarkup : undefined);

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || !!existingProduct;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={t("button.update_line_item")}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page footer={footerMarkup}>
      <View>
        {watchedProductId ? (
          <ProductSummaryCard
            productId={watchedProductId}
            locationId={invoiceArrival.locationId}
            onPress={() =>
              navigation.navigate("EditProductRootScreen", {
                productId: watchedProductId,
                screen: "EditProductScreen",
              })
            }
          />
        ) : (
          <View>{bannerMarkup}</View>
        )}
        <Divider />
      </View>
      <InvoiceArrivalItemForm />
    </Page>
  );
};

export const EditInvoiceLineItemScreen = (
  props: EditInvoiceLineItemScreenProps
) => {
  const { item: lineItem } = useActiveInvoiceArrivalItemProvider();

  return <EditInvoiceLineItem lineItem={lineItem} {...props} />;
};
